// onboarding-api.ts — the PROGRAMMATIC seam over the onboarding engine
// (WORK ITEM 63 PR 3, designed for item 64's assistant agent).
//
// THE STABLE CONTRACT (documented in docs/onboarding-overlays.md — a future
// assistant binds to exactly these three calls):
//
//   spotlight(target, { title, copy, placement? })  → FlowHandle | null
//     Highlight part of the live screen and say something about it: one
//     fixed scrim, the target(s) cut out crisp and clickable, one card with
//     the given text and a Done button (single step — no dots, no skip).
//     `target` is a DOM id, a SpotTarget, or an array of either.
//
//   startFlow(flowId)                               → FlowHandle | null
//     Run a REGISTERED flow (the same definitions the first-visit triggers
//     use) on demand — "walk me through this page again".
//
//   dismiss()
//     Clear whatever overlay is up. Safe when nothing is.
//
// Deliberate properties (each a decision):
//   - TEXT IN, NEVER MARKUP: title/copy render via textContent end to end —
//     this seam accepts runtime-supplied strings from an agent.
//   - NO PERSISTENCE: a programmatic run neither reads nor writes the
//     seen-state seat. Re-running a tour for the user must not depend on
//     their first-visit flags, and a demo must not silently consume a
//     first-visit. Callers that WANT persistence compose with the store
//     (onboarding-flows.ts) themselves.
//   - ONE OVERLAY AT A TIME, first committed wins (engine invariant): a
//     spotlight while a tour is live refuses and returns null — callers
//     check the return, or dismiss() first.
//   - The engine's whole measurement discipline (re-measure, auto-skip on
//     missing targets, reduced motion) applies unchanged.

import {
  activeFlowId,
  dismissActive,
  startFlow as engineStartFlow,
  type CardPlacement,
  type FlowDef,
  type FlowHandle,
  type OnboardingHooks,
  type SpotTarget,
} from './onboarding.js';
import { getFlow } from './onboarding-flows.js';

export interface SpotlightOpts {
  title: string;
  copy: string;
  placement?: CardPlacement;
}

export type SpotlightTarget = string | SpotTarget | Array<string | SpotTarget>;

/** Normalize the accepted target shapes into SpotTarget[]. Pure — drilled. */
export function spotlightDef(target: SpotlightTarget, opts: SpotlightOpts): FlowDef {
  const list = Array.isArray(target) ? target : [target];
  const targets: SpotTarget[] = list.map((t) => (typeof t === 'string' ? { id: t } : t));
  return {
    id: `spotlight:${targets.map((t) => t.id).join('+') || 'modal'}`,
    surface: '*',
    steps: [{ targets, title: opts.title, body: opts.copy, placement: opts.placement }],
  };
}

/**
 * Highlight `target` on the live screen with an instruction card.
 * Returns null when refused (another overlay is up, or no DOM).
 */
export function spotlight(
  target: SpotlightTarget,
  opts: SpotlightOpts,
  hooks: OnboardingHooks = {},
): FlowHandle | null {
  return engineStartFlow(spotlightDef(target, opts), hooks);
}

/**
 * Run a registered flow by id, on demand. No persistence — the first-visit
 * lane (onboarding-flows.ts) owns seen-state. Unknown ids log and return
 * null rather than throwing: the caller is possibly an agent mid-turn.
 */
export function startFlow(flowId: string, hooks: OnboardingHooks = {}): FlowHandle | null {
  const log = hooks.log ?? ((l: string) => console.info(l));
  const def = getFlow(flowId);
  if (!def) {
    log(`[onboarding] startFlow('${flowId}'): unknown flow id — nothing started`);
    return null;
  }
  return engineStartFlow(def, hooks);
}

/** Clear whatever overlay is up. Safe when nothing is. */
export function dismiss(): void {
  dismissActive('dismissed');
}

/** The flow currently on screen (spotlights report their synthetic id). */
export function activeFlow(): string | null {
  return activeFlowId();
}

/** The window seam each client installs at boot (window.helmOnboarding) —
 * the runtime-reachable surface item 64's assistant drives. */
export interface OnboardingWindowApi {
  spotlight: typeof spotlight;
  startFlow: typeof startFlow;
  dismiss: typeof dismiss;
  activeFlow: typeof activeFlow;
}

export function installWindowApi(): void {
  if (typeof window === 'undefined') return;
  const api: OnboardingWindowApi = { spotlight, startFlow, dismiss, activeFlow };
  (window as unknown as { helmOnboarding: OnboardingWindowApi }).helmOnboarding = api;
}
